"use client";

import React, { useMemo } from "react";
import ListingSlider from "@/src/shared/components/listing/ListingSlider";
import {
  BOOST_FROM_PAGE,
  FEATURED_MIN_TO_SHOW,
  FEATURED_TITLE,
  boostService,
  useFeaturedStaysQuery,
  type ISponsoredListing,
} from "@/src/shared/features/boost";
import type { ICampaignListing } from "@/src/shared/types/listing";
import type { IHomepageTab } from "../types";
import HomeRailsSkeleton from "./skeletons/HomeRailsSkeleton";

interface IFeaturedStaysRailProps {
  tab: IHomepageTab;
  href: string;
  onOpen: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * The app's "Featured Stays" row (`SponsoredListingsSection`): the tab's boosted stays
 * over the next two nights, above the other rails. It stays out of the way until there
 * are at least three, and every card tells the listing page which boost it came through.
 * While it loads (from the server's HTML on) a one-rail skeleton holds its place, so the
 * rails below don't jump down when it arrives.
 */
const FeaturedStaysRail: React.FC<IFeaturedStaysRailProps> = ({
  tab,
  href,
  onOpen,
}) => {
  const campaign = boostService.campaignSlugForTab(tab.slug);
  const query = useMemo(
    () => (campaign ? boostService.homeQuery(campaign, new Date()) : null),
    [campaign],
  );
  const { data, isPending } = useFeaturedStaysQuery(query);
  const items = useMemo(() => data?.data ?? [], [data]);
  const byId = useMemo(
    () => new Map<number, ISponsoredListing>(items.map((i) => [i.id, i])),
    [items],
  );

  // A tab without a campaign has no query, which TanStack also reports as pending.
  if (campaign && isPending) return <HomeRailsSkeleton rails={1} />;
  if (items.length < FEATURED_MIN_TO_SHOW) return null;

  const cardQuery = (card: ICampaignListing) => {
    const item = byId.get(card.listing.id);
    return item
      ? boostService.linkQuery(item, BOOST_FROM_PAGE.home(tab.slug))
      : undefined;
  };

  return (
    <div className="mb-8">
      <ListingSlider
        homeRow
        title={FEATURED_TITLE}
        listings={items.map(boostService.toCardListing)}
        sponsored
        href={href}
        onOpen={onOpen}
        cardQuery={cardQuery}
      />
    </div>
  );
};

export default FeaturedStaysRail;
